import { useQuery } from "@tanstack/react-query";
import { SignedUrlImage } from "../../../components/SignedUrlImage";
import { useApp } from "../../../services/app-context";
import { api as badgeCenterApi } from "../../../services/apis/badge-center";
import { useUser } from "../../../services/user-context";
import { ReactComponent as BadgeOutline } from "../../../assets/icons/badge-outline.svg";

type XpBreakdownComponent = {
  title: string;
  score: number;
  icon_url: string;
};

type XpBreakdown = {
  components: XpBreakdownComponent[];
  total: number;
  allowed: number;
  daily_xp_cap: number;
  earned_xp: number;
  xp_after: number;
  xp_before: number;
  daily_cap_reached: boolean;
};

/** Split bucket-root `icon_url` (e.g. `badge-center/xp_breakdown/star.svg`) for SignedUrlImage. */
function splitIconUrl(iconUrl: string): { path: string; s3Key: string } {
  const slash = iconUrl.lastIndexOf("/");
  if (slash === -1) return { path: "app/assets/images", s3Key: iconUrl };
  return { path: iconUrl.slice(0, slash), s3Key: iconUrl.slice(slash + 1) };
}

export function XPReportView({ sessionId }: { sessionId?: string }) {
  const { userInfo } = useUser() as { userInfo?: { id?: string } };
  const { tenantProfile } = useApp();
  const userId = userInfo?.id;

  const { data: xpBreakdown } = useQuery({
    queryKey: ["xp-breakdown", userId, sessionId],
    queryFn: () => badgeCenterApi.getXpBreakdown(sessionId!),
    enabled: !!userId && !!sessionId,
    refetchOnWindowFocus: false,
  });

  const components = (xpBreakdown as XpBreakdown | undefined)?.components;
  if (!sessionId || !userId || !components?.length) {
    return null;
  }
  
  const { daily_xp_cap, earned_xp, xp_after, daily_cap_reached } = xpBreakdown as XpBreakdown;
  const overallProgressPercentage =
    daily_cap_reached || daily_xp_cap <= 0
      ? 100
      : Math.min(100, Math.max(0, (xp_after / daily_xp_cap) * 100));
  return (
    <div className="bg-white shadow-[0px_4px_24px_0px_rgba(203,90,90,0.05)] p-5 rounded-[14px]">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm text-text-primary">XP Earned this session</div>
        <div className="bg-green-50 rounded-[20px] px-[9px] py-[1px]">
          <span className="text-[11px] font-normal  text-green-700 leading-[16px]">
            <span className="!text-[13px] leading-[16px] !font-semibold">+{earned_xp}</span>XP
          </span>
        </div>
      </div>
      <div className="flex items-start gap-2 w-full my-2.5">
        <div className="bg-orange-100 rounded-[6px] w-6 h-6 flex items-center justify-center">
          <BadgeOutline />
        </div>
        <div className="flex flex-col flex-1 gap-[3px] justify-center">
          <div className="text-xs font-semibold text-black">Daily {daily_xp_cap} XP limit</div>
          <div className="flex items-center gap-4 justify-center">
            <div
              className={`flex-1 flex-1 h-1.5 overflow-hidden rounded-full bg-[#ECF4EF]`}
            >
              <div
                className={`h-1.5 rounded-full bg-green-700`}
                style={{
                  width: `${overallProgressPercentage}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-2 mt-0.5">
        <span className="text-xs font-semibold text-black">XP Breakdown</span>
      </div>
      <div className="flex flex-col gap-2.5">
        {components.map((component, index) => {
          const icon = component.icon_url ? splitIconUrl(component.icon_url) : null;
          return (
            <div
              key={`${component.title}-${index}`}
              className="flex items-center gap-3"
            >
              {icon ? (
                <SignedUrlImage
                  s3Key={icon.s3Key}
                  path={icon.path}
                  bucket={tenantProfile?.assets_bucket}
                  alt=""
                  className="w-5 h-5"
                />
              ) : null}
              <div className="flex flex-1 items-center text-xs text-black justify-between gap-2 min-w-0">
                <span>{component.title}</span>
                <span>+{component.score}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
